import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { supabaseClient } from "../utils/supabase";
import { getStoredAdminPassword } from "../stores/adminAccessStore";

export interface AdminProfileSummary {
    profile_id: string;
    phone: string;
    profile_index: number;
    study_number: number | null;
    created_at: string;
    submitted_count: number;
    total_entries: number;
}

interface AdminDashboardResponse {
    enrolledProfiles: AdminProfileSummary[];
    pendingProfiles: AdminProfileSummary[];
}

export function useAdminDashboard(
    enabled: boolean,
    options?: Omit<UseQueryOptions<AdminDashboardResponse>, "queryKey" | "queryFn">
) {
    return useQuery({
        queryKey: ["admin-dashboard"],
        queryFn: async (): Promise<AdminDashboardResponse> => {
            const adminPassword = getStoredAdminPassword();
            if (!adminPassword) {
                throw new Error("Admin password required");
            }

            const { data, error } = await supabaseClient.functions.invoke<AdminDashboardResponse>("admin-dashboard", {
                body: { adminPassword },
            });

            if (error) {
                throw error;
            }

            if (!data || !Array.isArray(data.enrolledProfiles) || !Array.isArray(data.pendingProfiles)) {
                throw new Error("Invalid response from admin dashboard");
            }

            return data;
        },
        enabled,
        staleTime: 1000 * 60,
        ...options,
    });
}
